import React, { useState } from 'react'
import { DurationButton } from './DurationButton.js'
import {
    Loader2,
    Check,
    AlertTriangle,
    Trash2,
    Calendar,
} from 'lucide-react'
import { RoomModel } from '../../common/models/RoomModel.js'
import { RoomStatus } from '../../common/types.js'
import { bookRoom } from '../utils/calendar-utils.js'

export interface RoomCardProps {
    room: RoomModel
}

export function RoomCard({ room }: RoomCardProps) {
    const [selectedDuration, setSelectedDuration] = useState<number>(30)
    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)

    const handleBookNow = async (duration: number) => {
        setSelectedDuration(duration)
        setIsLoading(true)
        setIsSuccess(false)
        setError(null)

        try {
            // Use the centralized bookRoom function
            const response = await bookRoom(
                room.id,
                new Date(), // Current time as start time
                duration,
                `Booked from card`,
            )

            if (!response.ok) {
                throw new Error('Failed to book room')
            }

            setIsSuccess(true)
            // Reset success state after 3 seconds
            setTimeout(() => {
                setIsSuccess(false)
            }, 3000)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to book room')
        } finally {
            setIsLoading(false)
        }
    }

    const handleDeleteBooking = async () => {
        if (!room.currentBooking) return

        setIsDeleting(true)
        setError(null)

        try {
            const response = await fetch(`/api/v1/rooms/${room.id}/events/${room.currentBooking.id}`, {
                method: 'DELETE',
            })

            if (!response.ok) {
                throw new Error('Failed to delete booking')
            }

            // Success message will be handled by the room update from the server
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete booking')
        } finally {
            setIsDeleting(false)
        }
    }

    // Get minutes until next meeting from the room data
    const minutesUntilNextMeeting = room.timeUntilNextMeeting
    const hasTimeConstraint =
        minutesUntilNextMeeting !== undefined && minutesUntilNextMeeting < selectedDuration

    const isOccupied = room.status === 'occupied'
    const containerClass = isOccupied ? 'border border-[#c8c8c8]' : 'bg-white'
    const booking = room.currentBooking

    return (
        <div className={`rounded-[20px] ${containerClass} w-full md:w-auto`}>
            <div className="p-4 border-b border-[#e5e5e5]">
                <div className="flex justify-between items-center">
                    <h3 className="text-lg font-bold">
                        <a href={`#room=${room.id}`} className="text-neutral-900 hover:underline">
                            {room.name}
                        </a>
                    </h3>
                    {room.status === RoomStatus.OCCUPIED ? (
                        <div className="px-2 py-1 bg-rose-100 border border-[#ffccd3] text-[#a50036] text-xs rounded">
                            In Use
                        </div>
                    ) : (
                        <div className="px-2 py-1 bg-[#d0fae5] border border-[#a4f4cf] text-[#006045] text-xs rounded">
                            Available
                        </div>
                    )}
                </div>
            </div>

            {!room.isBookable ? (
                <div className="p-6 text-center text-neutral-500">Not bookable</div>
            ) : isOccupied && booking ? (
                <div className="p-6">
                    <div className="space-y-1">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1 text-[13px]">
                                <Calendar size={16} />
                                <span className="text-neutral-900">{room.currentBooking?.userName}</span>
                            </div>
                            {booking.canDelete(room.email) && (
                                <button
                                    onClick={handleDeleteBooking}
                                    disabled={isDeleting}
                                    className="text-neutral-500 hover:text-red-500 transition-colors"
                                    title="Delete booking"
                                >
                                    {isDeleting ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Trash2 className="w-4 h-4" />
                                    )}
                                </button>
                            )}
                        </div>
                        <div className="space-y-1">
                            <div className="text-neutral-500 text-[13px]">
                                {Math.ceil((new Date(room.currentBooking.endTime).getTime() - new Date().getTime()) / 60000)} min remaining (until {
                                new Date(room.currentBooking.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            })
                            </div>

                            {room.currentBooking.isRoomEvent ? (
                                <div className="text-neutral-500 text-[13px]">
                                    Organizer: TimeHub
                                </div>
                            ) : (
                                <div className="text-neutral-500 text-[13px]">
                                    Organizer: {room.currentBooking.organizerEmail} (+{room.currentBooking.attendeeCount})
                                </div>
                            )}
                        </div>
                    </div>
                    {error && (
                        <div className="mt-4 flex justify-center">
                            <div className="flex items-center gap-2 text-red-600 text-sm">
                                <AlertTriangle className="w-4 h-4" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="px-6 py-4">
                    {room.nextBooking && (
                        <div className="space-y-1">
                            <div className="flex items-center gap-1 text-[13px]">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.33333 1.33333V4" stroke="#737373" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M10.6667 1.33333V4" stroke="#737373" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12.6667 2.66667H3.33333C2.59695 2.66667 2 3.26362 2 4V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V4C14 3.26362 13.403 2.66667 12.6667 2.66667Z" stroke="#737373" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M2 6.66667H14" stroke="#737373" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span className="text-neutral-900">Next meeting:</span>
                            </div>
                            <div className="space-y-1">
                                <div className="text-neutral-500 text-[13.5px]">
                                    {new Date(room.nextBooking.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}-
                                    {new Date(room.nextBooking.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                <div className="flex items-center gap-1">
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11Z" stroke="#737373" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M6 3V6L8 7" stroke="#737373" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className="text-neutral-500 text-[13.4px]">In {minutesUntilNextMeeting} minutes</span>
                                </div>
                                <div className="text-neutral-900 text-[13.1px]">{room.nextBooking.userName}</div>
                            </div>
                        </div>
                    )}

                    {minutesUntilNextMeeting !== undefined && minutesUntilNextMeeting < 60 && (
                        <div className="flex items-center gap-1 mt-4 text-[#e17100] text-[13.3px]">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.4866 12.0002L9.15329 2.66682C9.037 2.46162 8.86836 2.29094 8.66457 2.1722C8.46078 2.05345 8.22915 1.99089 7.99329 1.99089C7.75743 1.99089 7.52579 2.05345 7.322 2.1722C7.11822 2.29094 6.94958 2.46162 6.83329 2.66682L1.49995 12.0002C1.38241 12.2037 1.32077 12.4348 1.32129 12.6698C1.32181 12.9049 1.38447 13.1357 1.50292 13.3387C1.62136 13.5417 1.79138 13.7099 1.99575 13.826C2.20011 13.9422 2.43156 14.0023 2.66662 14.0002H13.3333C13.5672 13.9999 13.797 13.9381 13.9995 13.821C14.202 13.7039 14.3701 13.5355 14.487 13.3329C14.6038 13.1302 14.6653 12.9004 14.6653 12.6665C14.6652 12.4325 14.6036 12.2027 14.4866 12.0002Z" stroke="#E17100" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M8 6V8.66667" stroke="#E17100" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M8 11.3333H8.00667" stroke="#E17100" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span>Limited time: Next meeting in {minutesUntilNextMeeting} minutes</span>
                        </div>
                    )}

                    <div className="flex items-center self-center">
                        <div className="pt-4 flex flex-col justify-center flex-grow">
                            <div className="grid grid-cols-2 sm:grid-cols-2 gap-[11px] w-full">
                                <DurationButton
                                    duration={15}
                                    isDisabled={isLoading || hasTimeConstraint && 15 > (minutesUntilNextMeeting || 0)}
                                    onClick={() => handleBookNow(15)}
                                />
                                <DurationButton
                                    duration={30}
                                    isDisabled={isLoading || hasTimeConstraint && 30 > (minutesUntilNextMeeting || 0)}
                                    onClick={() => handleBookNow(30)}
                                />
                                <DurationButton
                                    duration={45}
                                    isDisabled={isLoading || hasTimeConstraint && 45 > (minutesUntilNextMeeting || 0)}
                                    onClick={() => handleBookNow(45)}
                                />
                                <DurationButton
                                    duration={55}
                                    isDisabled={isLoading || hasTimeConstraint && 55 > (minutesUntilNextMeeting || 0)}
                                    onClick={() => handleBookNow(55)}
                                />
                            </div>

                            {isLoading && (
                                <div className="mt-4 flex justify-center">
                                    <div className="flex items-center gap-2 text-gray-600">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span>Booking room...</span>
                                    </div>
                                </div>
                            )}

                            {isSuccess && (
                                <div className="mt-4 flex justify-center">
                                    <div className="flex items-center gap-2 text-green-600">
                                        <Check className="w-4 h-4" />
                                        <span>Room booked successfully!</span>
                                    </div>
                                </div>
                            )}

                            {error && (
                                <div className="mt-4 flex justify-center">
                                    <div className="flex items-center gap-2 text-red-600 text-sm">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span>{error}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
